import { Types } from '@amplitude/analytics-browser';
import { Experiment, ExperimentClient, Variants } from '@amplitude/experiment-js-client';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import React, { ReactNode, useEffect, useState } from 'react';

import { getAgentDetailsFromEtsToken } from '../../resources/login/operations';
import { getEnv } from '../utilities/env';

import { ampli, ApiKey } from './__AUTO_GENERATED__';

function getAmplitudeApiKey() {
    const appEnv = getEnv('APP_ENV');

    // `test` = environnement des tests E2E ou unit : pas de SDK Amplitude
    if (!appEnv || appEnv === 'test') {
        return null;
    }

    if (appEnv === 'production') {
        return ApiKey.production;
    }

    return ApiKey.development;
}

function getExperimentDeployKey(): string {
    const deployKey = getEnv('AMPLITUDE_EXPERIMENT_DEPLOY_KEY');

    if (!deployKey) {
        throw new Error('Amplitude Experiment missing a deploy key');
    }

    return deployKey;
}

// Debug standalone : un log lisible par event tracké (l'extension Event Explorer est peu
// fiable sur localhost). Ajouté uniquement en APP_ENV=local — voir le `.then` du load.
function createStandaloneEventLogger(): Types.EnrichmentPlugin {
    return {
        name: 'standalone-event-logger',
        type: 'enrichment',
        execute: async (event) => {
            // eslint-disable-next-line no-console -- debug local standalone uniquement
            console.log(
                `%c[amplitude] ${event.event_type}`,
                'color:#7c3aed;font-weight:bold',
                event.event_properties ?? {},
            );
            return event;
        },
    };
}

type AmplitudeContextValue = {
    isAmplitudeLoaded: boolean;
    client: Types.BrowserClient | null;
    experiment: ExperimentClient | null;
    variants: Variants;
};

export const AmplitudeContext = React.createContext<AmplitudeContextValue>({
    experiment: null,
    isAmplitudeLoaded: false,
    client: null,
    variants: {},
});

export const AmplitudeProvider = ({ children }: { children?: ReactNode }) => {
    const [isAmplitudeLoaded, setIsAmplitudeLoaded] = useState(ampli.isLoaded);

    const amplitudeApiKey = getAmplitudeApiKey();
    const [experiment, setExperiment] = useState<ExperimentClient | null>(null);
    const [variants, setVariants] = useState<Variants>({});

    useEffect(() => {
        if (amplitudeApiKey && !isAmplitudeLoaded) {
            void ampli
                .load({
                    client: {
                        configuration: {
                            serverZone: 'EU',
                            defaultTracking: {
                                pageViews: false,
                                sessions: true,
                            },
                            autocapture: {
                                formInteractions: false,
                            },
                        },
                        apiKey: amplitudeApiKey,
                    },
                })
                .promise.then(() => {
                    if (getEnv('APP_ENV') === 'production') {
                        ampli.client.add(
                            sessionReplayPlugin({
                                sampleRate: 0.1,
                            }),
                        );
                    }

                    const appEnv = getEnv('APP_ENV');
                    if (appEnv === 'local' || appEnv === 'staging') {
                        ampli.client.add(createStandaloneEventLogger());
                    }

                    const experimentInstance = Experiment.initializeWithAmplitudeAnalytics(
                        getExperimentDeployKey(),
                        {
                            serverUrl: 'https://api.lab.eu.amplitude.com',
                        },
                    );
                    setExperiment(experimentInstance);

                    return experimentInstance.fetch().then(() => {
                        setVariants(experimentInstance.all());
                        setIsAmplitudeLoaded(true);
                    });
                });
        }
    }, [amplitudeApiKey, isAmplitudeLoaded]);

    useEffect(() => {
        if (isAmplitudeLoaded) {
            const agentsDetails = getAgentDetailsFromEtsToken();
            if (agentsDetails) {
                // These are the Amplitude user_properties that can be used for user segmentation/composition
                ampli.identify(agentsDetails?.userId.toString(), {
                    agency_id: agentsDetails.agencyId,
                    agency_name: agentsDetails.agencyName,
                });
            }
        }
    }, [isAmplitudeLoaded]);

    return (
        <AmplitudeContext.Provider
            value={{
                isAmplitudeLoaded,
                client: isAmplitudeLoaded ? ampli.client : null,
                experiment,
                variants,
            }}
        >
            {children}
        </AmplitudeContext.Provider>
    );
};
